import { useState } from 'react';
import type {
   ComponentType,
   PropsWithChildren,
   InputHTMLAttributes,
   LiHTMLAttributes,
   MouseEvent as ReactMouseEvent,
   CSSProperties,
   ReactNode,
   Ref,
} from 'react';
import initializeComponent from 'Shared/react-initialize';
import { withBreakpointsContext } from 'Shared/utils/breakpoints';
import { _js } from '@ifixit/utils/localize';
import styled from 'styled-components';
import { color, space, fontSize, shadow, borderRadius } from '@ifixit/primitives';
import { FaIcon } from '@ifixit/ui/components/icon';
import LoadingCircle from 'Shared/icons/loading-circle.svg';
import { multiSearchAlgolia } from 'Guide/Search/multisearch_algolia';
import { useCombobox } from 'downshift';
import { getSearchClient, SearchClientType } from 'Guide/searchClient';
import { getInputFormValue, setInputFormValue } from 'Shared/ifixit_store';
import { getNewPageURL } from 'Guide/get_new_page_url';

const HITS_PER_PAGE = 5;

/**
 * This is the hit result that is returned from Algolia.
 * It is a combination of only the attributes we use.
 */
export type HitType = {
   device: string;
   title: string;
   url: string;
   objectID: string;
   image_url: string;
   description: string;
};

/*
 * This is a custom component that is used to render the wiki search results as the user types.
 * This component runs off of algolia instantsearch, but can render a functional input form if
 * algolia is not set up on the site.
 * @param namespace - the namespace to search in
 * @param placeHolder - the placeholder text to display in the search box
 * @param buttonText - the translated text to display on the search button. If not provided, no button will be displayed.
 **/
export function WikiSearch({
   namespace,
   placeHolder,
   buttonText,
   createType,
   formTabIndex,
   buttonTabIndex,
   styledSearchBarContainer,
   styledSearchResultsContainer,
   inputClassName,
   isInvalidTitle = false,
   disableButtonWhenEmpty = false,
   onNewDeviceSelected,
   onKnownDeviceSelected,
   onFocus,
   onBlur,
}: {
   namespace: string;
   placeHolder: string;
   buttonText?: string;
   createType?: string;
   formTabIndex?: number;
   buttonTabIndex?: number;
   /* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
   styledSearchBarContainer?: ComponentType<PropsWithChildren<{}>>;
   /* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
   styledSearchResultsContainer?: ComponentType<PropsWithChildren<{}>>;
   inputClassName?: string;
   isInvalidTitle?: boolean;
   /* Grey out the button while the box is empty — for callers where the
      button commits the current entry (e.g. saving a wiki's parent) rather
      than creating whatever was typed. */
   disableButtonWhenEmpty?: boolean;
   onNewDeviceSelected: onSubmitType;
   onKnownDeviceSelected: onClickType;
   onFocus?: () => void;
   onBlur?: (e: any) => void;
}) {
   const langId = App.lang;
   const localIndex = App.isDev ? 'dev_' + namespace + '_' + langId : namespace + '_' + langId;
   const enIndex = App.isDev ? 'dev_' + namespace + '_en' : namespace + '_en';

   if (!createType) {
      // eslint-disable-next-line react-hooks/immutability -- TODO: grandfathered in react-hooks 7.1 / ESLint 10 upgrade
      createType = namespace;
   }
   const [queryResults, setQueryResults] = useState<HitType[]>([]);
   const [loading, setLoading] = useState(false);
   const searchClient: SearchClientType = getSearchClient();
   const SearchBarContainer = styledSearchBarContainer || DefaultSearchBarContainer;

   async function updateResults(query: string) {
      await multiSearch(query, localIndex, enIndex, searchClient).then(results => {
         setQueryResults(results);
         setLoading(false);
      });
   }

   const {
      isOpen,
      getMenuProps,
      getInputProps,
      highlightedIndex,
      getItemProps,
      setInputValue,
      inputValue,
   } = useCombobox({
      items: queryResults,
      // @ts-expect-error TS(2322) FIXME: Type '(item: HitType) => string' is not assignable... Remove this comment to see the full error message
      itemToString: (item: HitType) => (item ? item.title : ''),
      onInputValueChange: ({ inputValue }: { inputValue?: string }) => {
         // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
         setInputFormValue(inputValue);
         // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
         setInputValue(inputValue);
         if (inputValue) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
            updateResults(inputValue);
         }
      },
      onSelectedItemChange: ({ selectedItem }: { selectedItem?: HitType }) => {
         if (selectedItem) {
            onKnownDeviceSelected({ selectedItem, createType });
         }
      },
   });

   // Always the same component, hidden with a style: swapping it for a
   // DisplayNone component remounts the menu <ul> on every open/close, and
   // downshift's outside-press detection captures the menu node once — a
   // remounted menu makes it treat presses on the items as outside, closing
   // the menu on mouseup so the click never lands on an item.
   const showResults = (isOpen && queryResults.length > 0) || loading;
   const SearchResultsContainer = styledSearchResultsContainer || DefaultSearchResultsContainer;

   return (
      <div>
         <SearchBarContainer>
            <CustomSearchBox
               {...getInputProps()}
               setInputValue={setInputValue}
               onFocus={onFocus}
               onBlur={onBlur}
               placeholder={placeHolder}
               inputClassName={inputClassName}
               formType={'search'}
               tabIndex={formTabIndex}
               isInvalidTitle={isInvalidTitle}
            />
            <CreateButton
               namespace={createType}
               buttonText={buttonText}
               handleSubmit={onNewDeviceSelected}
               tabIndex={buttonTabIndex}
               query={inputValue}
               disabled={disableButtonWhenEmpty && !(inputValue ?? '').trim()}
            />
         </SearchBarContainer>
         <div style={showResults ? undefined : { display: 'none' }}>
            <SearchResultsContainer>
               <ConditionalLoadingCircle loading={loading} />
               <NoStyleList {...getMenuProps()}>
                  {isOpen &&
                     queryResults.map((item, index) => (
                        <HitItem
                           hit={item}
                           createType={createType}
                           style={highlightedIndex === index ? { backgroundColor: '#bde4ff' } : {}}
                           key={`${item.title}${index}`}
                           {...getItemProps({
                              item,
                              index,
                              // The stretched overlay link must not navigate on
                              // a plain click — downshift's ItemClick handles
                              // selection (fills the input, closes the menu,
                              // fires onSelectedItemChange exactly once).
                              onClick: event => event.preventDefault(),
                           })}
                        />
                     ))}
               </NoStyleList>
            </SearchResultsContainer>
         </div>
      </div>
   );
}

async function multiSearch(
   query: string,
   localIndex: string,
   enIndex: string,
   searchClient: SearchClientType
): Promise<any> {
   const indices = localIndex === enIndex ? [localIndex] : [localIndex, enIndex];
   const results = await multiSearchAlgolia<HitType>(searchClient, indices, query, HITS_PER_PAGE);
   return results;
}

function ConditionalLoadingCircle({ loading }: { loading: boolean }) {
   if (!loading) {
      return null;
   }
   return (
      <span>
         <LoadingCircle />
      </span>
   );
}

interface CustomSearchBoxProps extends InputHTMLAttributes<HTMLInputElement> {
   placeHolder: string;
   inputClassName?: string;
   formType: string;
   tabIndex?: number;
   isInvalidTitle: boolean;
   setInputValue: (value: string) => void;
}

function CustomSearchBox({
   ref,
   ...props
}: CustomSearchBoxProps & { ref?: Ref<HTMLInputElement> }) {
   const input = props.value || getInputFormValue() || '';
   const { isInvalidTitle, formType, inputClassName, setInputValue, ...otherProps } = props;
   return (
      <div className="relative flex h-full w-full">
         <PageTypeTitleInput
            $isInvalidTitle={isInvalidTitle}
            className={inputClassName + ' introDevice'}
            tabIndex={props.tabIndex}
            required
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            placeholder={_js(props.placeHolder)}
            spellCheck="false"
            maxLength={512}
            type={formType}
            title=""
            value={input}
            ref={ref}
            {...otherProps}
         />
         <div
            className="absolute top-0 right-[2px] h-full w-[30px] items-center justify-center pr-[10px]"
            style={{ display: input ? 'flex' : 'none' }}
         >
            <ClearButton
               onMouseDown={(event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
                  event.preventDefault();
                  setInputValue('');
                  setInputFormValue('');
               }}
            >
               <FaIcon name="xmark" style="regular" className="size-4" />
            </ClearButton>
         </div>
      </div>
   );
}

/**
 * Provides similar functionality to less' darken() function.
 */
function darkenColor({ hex, amt }: { hex: string; amt: number }) {
   hex = hex.slice(1);
   let color: number = Number.parseInt(hex, 16);
   color =
      ((color & 0x00_00_ff) + amt) |
      ((((color >> 8) & 0x00_ff) + amt) << 8) |
      (((color >> 16) + amt) << 16);
   return '#' + color.toString(16);
}

function CreateButton({
   namespace,
   buttonText,
   handleSubmit,
   tabIndex,
   query,
   disabled,
}: {
   namespace: string;
   buttonText?: string;
   handleSubmit: onSubmitType;
   tabIndex?: number;
   query: string;
   disabled?: boolean;
}) {
   if (!buttonText) {
      return null;
   }
   return (
      <StyledButton
         tabIndex={tabIndex}
         className="create-button"
         disabled={disabled}
         onClick={(e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            handleSubmit({ query, namespace });
         }}
      >
         {buttonText}
      </StyledButton>
   );
}

type HitProps = {
   hit: HitType;
   createType: string;
   style: CSSProperties;
   children?: ReactNode;
};

type onSubmitType = ({ query, namespace }: { query?: string; namespace?: string }) => void;

type onClickType = ({
   selectedItem,
   query,
   createType,
}: {
   selectedItem: HitType;
   query?: string;
   createType: string;
}) => void;

function HitItem({
   ref,
   hit: selectedItem,
   createType,
   style,
   // downshift's item props (ref registration, ItemClick, hover highlight)
   ...downshiftItemProps
}: HitProps & { ref?: Ref<HTMLLIElement> } & LiHTMLAttributes<HTMLLIElement>) {
   return (
      <ListItemWrapper
         ref={ref}
         {...downshiftItemProps}
         className="dropdown-hit-item"
         style={{ ...style, position: 'relative' }}
      >
         {/* LinkOverlay: stretched link covering the item (plain clicks are
             prevented by the onClick composed into getItemProps) */}
         <a
            href={getNewPageURL(createType, selectedItem.device, selectedItem.url)}
            data-device={selectedItem.device}
            aria-label={selectedItem.title}
            className="absolute inset-0"
         />
         <div className="w-16">
            <img
               src={selectedItem.image_url}
               alt={selectedItem.title + ' search result image'}
               className="w-full"
               style={{ padding: '10px 4px 4px 8px' }}
            />
         </div>
         <div className="w-[calc(100%-64px)] flex-1 p-2">
            <h3 className="mt-0 overflow-hidden text-ellipsis whitespace-nowrap border-none text-base text-gray-900">
               {selectedItem.title}
            </h3>
            <p className="mt-0 text-gray-600 line-clamp-2">{selectedItem.description}</p>
         </div>
      </ListItemWrapper>
   );
}

const StyledButton = styled.button`
   flex: 0 0 auto;
   margin-left: -1px;
   border-radius: ${borderRadius['md']};
   border-top-left-radius: 0;
   border-bottom-left-radius: 0;
   color: white;
   background-color: ${color.brand[500]};
   position: relative;
   cursor: pointer;
   display: inline-block;
   font-size: ${fontSize['md']};
   text-align: center;
   height: 100%;
   padding-inline: ${space[3]};
   // Mobile Safari button-style override.
   -webkit-appearance: none;
   &:focus {
      box-shadow: ${shadow[1]};
   }
   &:active {
      background-color: ${darkenColor({ hex: color.brand[500], amt: -10 })};
   }
   &:hover {
      background-color: ${darkenColor({ hex: color.brand[500], amt: -5 })};
   }
   &:disabled {
      cursor: default;
      opacity: 0.5;
      background-color: ${color.brand[500]};
   }
`;

const DefaultSearchBarContainer = styled.div`
   display: flex;
   width: 100%;
   height: ${space[7]};
`;

const DefaultSearchResultsContainer = styled.div`
   border: ${color.gray[400]} 1px solid;
   box-shadow: ${shadow[3]};
   display: block;
   &:empty {
      border: none;
   }
`;

const ListItemWrapper = styled.li`
   display: flex;
   margin-bottom: 0 !important;
   background-color: white;
   max-width: 100%;
   max-height: 80px;
   list-style-type: none;
   &:hover {
      background-color: ${color.gray[100]};
      text-decoration: none;
   }
`;

const PageTypeTitleInput = styled.input<{ $isInvalidTitle: boolean }>`
   flex: 1 0 auto;
   width: 100%;
   height: 100%;
   padding: ${space[2]} ${space[3]};
   margin: ${space[0]};
   font-size: ${space[4]};
   border: 1px solid rgba(0, 0, 0, 0.38);
   border-radius: ${borderRadius['md']} 0 0 ${borderRadius['md']};
   outline: none;
   background-color: ${props => (props.$isInvalidTitle ? 'rgb(239, 208, 208)' : '#ffffff')};
   color: ${color.black};
   &:focus {
      border-color: ${color.brand[500]};
      box-shadow: ${space[0]} ${space[0]} ${space[1]} ${color.brand[500]};
   }
`;

const ClearButton = styled.div`
   width: 20px;
   height: 20px;
   flex: 0;
   padding-right: ${space[1]};
   padding-left: ${space[1]};
   background-color: ${color.gray[50]};
   cursor: pointer;
`;

const NoStyleList = styled.ul`
   list-style-type: none;
   padding: 0;
   margin: 0;
`;

initializeComponent('WikiSearch', withBreakpointsContext(WikiSearch));
